/* eslint-disable indent */
import { AuthContext } from '@core/shared';
import { EditWebinarData, OptionsApi } from '@core/swagger-client';
import { TEST_WEBINAR_MAX_DURATION_MINUTES, TEST_WEBINAR_MAX_VIEWERS, TEST_WEBINAR_DELETE_AFTER_START_HOURS, WebinarStatus } from '@core/types';
import { Button, Confirm, ContainerDefault, ErrorMessage, ErrorMessagePage, Input, LoaderPage, Switch, TestWebinarNotice, TextArea } from '@core/ui';
import { useNavigate, useParams } from '@online-webinar/router';
import { BackToOriginLink } from '@online-webinar/ui-frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime, Duration } from 'luxon';
import { useContext, useEffect, useState } from 'react';
import { FieldPath, FieldValues, SubmitHandler, ValidateResult, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { WebinarCardTitle } from '../../atoms';
import { WebinarCard } from '../../molecules';

type ValidateFields<TFieldValues extends FieldValues> = <TFieldName extends FieldPath<TFieldValues>>(fields: TFieldName[]) => Promise<boolean>;

const minDurationMinutes = 5;

export const CreateAndEditLiveWebinar = () => {
    const { t } = useTranslation();
    const nav = useNavigate();

    const { webinarId } = useParams({ strict: false });
    const queryClient = useQueryClient();

    const account = useContext(AuthContext).mainAccount;

    const [isValidating, setIsValidating] = useState(false);
    // Default to not-modifiable if webinarId is provided
    const [canModifyBooking, setCanModifyBooking] = useState(!webinarId);
    const [maxDuration, setMaxDuration] = useState('01:00');

    // Test webinar (spec §5.1). On /create the switch decides; on /edit the flag comes from the webinar
    // itself and can never change. The confirm dialog is the "explicit action" the requirement asks for.
    const [isTest, setIsTest] = useState(false);
    const [testConfirmShown, setTestConfirmShown] = useState(false);

    const {
        formState: { errors },
        register,
        reset,
        handleSubmit,
        getValues,
        setValue,
        trigger,
        watch
    } = useForm<EditWebinarData>({
        mode: 'onBlur',
        reValidateMode: 'onChange',
        defaultValues: {}
    });
    const onSubmit: SubmitHandler<EditWebinarData> = (data) => {};

    const {
        data: webinar,
        isLoading: webinarIsLoading,
        error: webinarError
    } = useQuery(
        OptionsApi.webinar.getEdit(webinarId!, {
            enabled: !!webinarId
        })
    );

    const {
        data: webinarStatus,
        isLoading: webinarStatusIsLoading,
        error: webinarStatusError
    } = useQuery(
        OptionsApi.webinar.getStatus(webinarId!, {
            enabled: !!webinarId
        })
    );

    const {
        data: webinarDetails,
        isLoading: webinarDetailsIsLoading,
        error: webinarDetailsError
    } = useQuery(
        OptionsApi.webinar.get(webinarId!, {
            enabled: !!webinarId
        })
    );

    const isTestWebinar = webinarId ? !!webinarDetails?.isTest : isTest;

    const { data: tiers, isLoading: tiersIsLoading, error: tiersError } = useQuery(OptionsApi.tier.getAvailable());

    const getApiValues = (data: EditWebinarData): EditWebinarData => {
        return {
            ...data,
            broadcastDateTime: DateTime.fromISO(data.broadcastDateTime).toUTC().toISO()!,
            plannedDuration: Duration.fromISOTime(data.plannedDuration).toISOTime()!
        };
    };

    useEffect(() => {
        if (!webinarId) return;

        // Expired webinars can be rescheduled: allow editing the date/time, which moves them back to Planned.
        const status = webinarStatus as WebinarStatus;
        setCanModifyBooking(status === WebinarStatus.Planned || status === WebinarStatus.Expired);
    }, [webinarId, webinarStatus]);

    useEffect(() => {
        if (!tiers) {
            return;
        }

        const duration = Duration.fromISOTime(tiers.length > 0 ? tiers[0].defaultDuration : '01:00:00');
        const maxDuration = Duration.fromISOTime(tiers.length > 0 ? tiers[0].duration : '01:00:00');
        setMaxDuration(maxDuration.toFormat('hh:mm'));

        if (webinar) {
            const broadcastDateTime = DateTime.fromISO(webinar.broadcastDateTime).toLocal().toISO({ includeOffset: false })!;
            const plannedDuration = Duration.fromISOTime(webinar.plannedDuration).toFormat('hh:mm');
            reset({
                ...webinar,
                broadcastDateTime,
                plannedDuration
            });
        }

        const tierId = tiers.length > 0 ? tiers[0].tierId : '00000000-0000-0000-0000-000000000000';
        if (!webinar && account) {
            reset({
                tierId: tierId,
                title: '',
                nameOfHost: account.profile.nickname || account.profile.firstName || account.profile.emailAddress,
                description: '',
                tags: [''],
                clientReference: '',
                plannedDuration: duration.toFormat('hh:mm')
            });
        }
    }, [webinar, tiers, account]);

    const isAvailableMutation = useMutation(OptionsApi.tier.isAvailable());
    const validateBroadcastDate = async (value: string, values: EditWebinarData): Promise<ValidateResult> => {
        if (!canModifyBooking) return true;

        try {
            const isAvailable = await isAvailableMutation.mutateAsync(getApiValues(values));
            if (!isAvailable) return t('Webinar.Schedule.Warning.Timeslot');
        } catch {
            return t('Webinar.Schedule.Warning.Timeslot');
        }

        // If the webinar is being edited and the date is unchanged, skip the future check — except for an
        // expired webinar, whose prefilled date is in the past and must be moved to the future to reschedule
        // (otherwise the user could "reschedule" without changing the date and the backend leaves it Expired).
        if (
            webinar &&
            (webinarStatus as WebinarStatus) !== WebinarStatus.Expired &&
            DateTime.fromISO(value).toUTC().equals(DateTime.fromISO(webinar.broadcastDateTime).toUTC())
        )
            return true;

        const date = new Date(value);
        const currentTime = new Date();
        currentTime.setSeconds(0, 0);
        if (date < currentTime) return t('Webinar.Schedule.Warning.DateFuture');
        return true;
    };

    const validatePlannedDuration = async (value: string, values: EditWebinarData): Promise<ValidateResult> => {
        if (!canModifyBooking) return true;

        // Local checks first: an invalid or out-of-range duration is refused without a server round
        // trip, so a prefilled but not-yet-dated form never surfaces a spurious timeslot error.
        const duration = Duration.fromISOTime(value);
        if (!duration.isValid) {
            return t('Webinar.Schedule.Warning.DurationInvalid');
        }

        if (duration.as('minutes') < minDurationMinutes) {
            return t('Webinar.Schedule.Warning.DurationMinimum', { minDuration: minDurationMinutes });
        }

        // A test webinar is capped at 15 minutes instead of the tier's maximum (spec §5.1); the server
        // applies the same rule, so the two never disagree.
        if (isTestWebinar) {
            if (duration.as('minutes') > TEST_WEBINAR_MAX_DURATION_MINUTES) {
                return t('Webinar.Schedule.Warning.TestDurationMaximum', { maxDuration: TEST_WEBINAR_MAX_DURATION_MINUTES });
            }
        } else if (duration > Duration.fromISOTime(maxDuration)) {
            return t('Webinar.Schedule.Warning.DurationMaximum', { maxDuration });
        }

        try {
            const isAvailable = await isAvailableMutation.mutateAsync(getApiValues(values));
            if (!isAvailable) return t('Webinar.Schedule.Warning.Timeslot');
        } catch {
            return t('Webinar.Schedule.Warning.Timeslot');
        }

        return true;
    };

    const validateFields = (async (fields) => {
        for (const field of fields) {
            if (!(await trigger(field))) {
                return false;
            }
        }
        return true;
    }) as ValidateFields<EditWebinarData>;

    const planWebinarMutation = useMutation(OptionsApi.webinar.plan());
    const planTestWebinarMutation = useMutation(OptionsApi.webinar.planTest());
    const editWebinarMutation = useMutation(OptionsApi.webinar.edit());

    const confirmTestWebinar = () => {
        setIsTest(true);
        // The cap is the sensible default; the host may shorten it. Not validating here avoids a
        // spurious timeslot error before a broadcast date is even entered — validation still runs
        // on blur/submit.
        setValue('plannedDuration', Duration.fromObject({ minutes: TEST_WEBINAR_MAX_DURATION_MINUTES }).toFormat('hh:mm'));
    };

    const onTestSwitchChange = (checked: boolean) => {
        // Switching on is a decision with consequences: ask first. Switching off needs no dialog.
        if (checked) setTestConfirmShown(true);
        else setIsTest(false);
    };

    const storeWebinar = () => {
        setIsValidating(true);

        validateFields(
            canModifyBooking
                ? ['title', 'description', 'broadcastDateTime', 'plannedDuration', 'nameOfHost', 'tags']
                : ['title', 'description', 'nameOfHost', 'tags']
        ).then((isValid) => {
            setIsValidating(false);

            if (!isValid) return;

            const mutation = webinarId ? editWebinarMutation : isTest ? planTestWebinarMutation : planWebinarMutation;
            mutation.mutate(getApiValues(getValues()), {
                onSuccess: (data) => {
                    queryClient.invalidateQueries({
                        predicate: (query) => {
                            // Invalidate any query that has webinarId in its queryKey and matches the current webinarId
                            for (var key of query.queryKey) {
                                if (typeof key === 'string' && key === data.webinarId!) {
                                    return true;
                                }
                            }
                            return false;
                        }
                    });

                    nav({ to: '/$webinarId/content', params: { webinarId: data.webinarId! } });
                }
            });
        });
    };

    if (webinarIsLoading || webinarStatusIsLoading || webinarDetailsIsLoading || tiersIsLoading) {
        return <LoaderPage />;
    }

    const error = webinarError ?? webinarStatusError ?? webinarDetailsError ?? tiersError;
    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    const mutationError = planWebinarMutation.error || planTestWebinarMutation.error || editWebinarMutation.error || isAvailableMutation.error;
    const isPending =
        isValidating || planWebinarMutation.isPending || planTestWebinarMutation.isPending || editWebinarMutation.isPending || isAvailableMutation.isPending;
    const formBroadcastDateTime = watch('broadcastDateTime');
    return (
        <ContainerDefault>
            <div data-testid="webinarsCreateAndEditForm" className="max-w-3xl">
                <BackToOriginLink fallback="overview" />

                <Confirm
                    title={t('Webinar.Test.Confirm.Title')}
                    open={testConfirmShown}
                    onClose={() => setTestConfirmShown(false)}
                    onConfirm={confirmTestWebinar}
                    closeText={t('Webinar.Test.Confirm.Button.Close')}
                    confirmText={t('Webinar.Test.Confirm.Button.Confirm')}
                    closeTestId="test-webinar-cancel"
                    confirmTestId="test-webinar-confirm"
                >
                    {t('Webinar.Test.Confirm.Description', {
                        maxDurationMinutes: TEST_WEBINAR_MAX_DURATION_MINUTES,
                        maxViewers: TEST_WEBINAR_MAX_VIEWERS,
                        deleteAfterStartHours: TEST_WEBINAR_DELETE_AFTER_START_HOURS
                    })}
                </Confirm>

                {isTestWebinar && (
                    <TestWebinarNotice
                        broadcastDateTime={
                            webinarId
                                ? webinarDetails?.broadcastDateTime
                                : formBroadcastDateTime && DateTime.fromISO(formBroadcastDateTime).isValid
                                  ? DateTime.fromISO(formBroadcastDateTime).toUTC().toISO()
                                  : null
                        }
                    />
                )}

                {mutationError && <ErrorMessage classNames="mb-8" apiError={mutationError} />}
                <div className="flex flex-col md:flex-row md:justify-between mb-4">
                    <WebinarCardTitle>{t(webinarId ? 'Webinar.Edit.PageHeading' : 'Webinar.Schedule.PageHeading')}</WebinarCardTitle>
                </div>
                <WebinarCard>
                    <form id="my-form" onSubmit={handleSubmit(onSubmit)}>
                        <Input
                            autoFocus
                            label={t('Webinar.Schedule.Label.Title')}
                            {...register('title', {
                                required: t('Webinar.Schedule.Warning.TitleRequired'),
                                minLength: { value: 3, message: t('Webinar.Schedule.Warning.TitleMin') },
                                maxLength: { value: 350, message: t('Webinar.Schedule.Warning.TitleMax') }
                            })}
                            errorMessage={errors.title?.message}
                        />

                        {!webinarId && (
                            <Switch
                                classNames="mb-6"
                                data-testid="test-webinar-switch"
                                label={t('Webinar.Test.Switch.Label')}
                                checked={isTest}
                                onChange={(e) => onTestSwitchChange(e.target.checked)}
                            />
                        )}

                        <div className="flex gap-4">
                            {canModifyBooking && (
                                <>
                                    <Input
                                        label={t('Webinar.Schedule.Label.BroadcastDate')}
                                        type="datetime-local"
                                        {...register('broadcastDateTime', {
                                            required: t('Webinar.Schedule.Warning.DateRequired'),
                                            validate: validateBroadcastDate
                                        })}
                                        errorMessage={errors.broadcastDateTime?.message}
                                    ></Input>
                                    <Input
                                        label={t('Webinar.Schedule.Label.Duration')}
                                        type="time"
                                        {...register('plannedDuration', {
                                            required: t('Webinar.Schedule.Warning.DurationRequired'),
                                            validate: validatePlannedDuration
                                        })}
                                        errorMessage={errors.plannedDuration?.message}
                                    ></Input>
                                </>
                            )}
                        </div>

                        <Input
                            label={t('Webinar.Schedule.Label.HostName')}
                            {...register('nameOfHost', {
                                required: t('Webinar.Schedule.Warning.NameRequired'),
                                minLength: { value: 3, message: t('Webinar.Schedule.Warning.NameMin') },
                                maxLength: { value: 350, message: t('Webinar.Schedule.Warning.NameMax') }
                            })}
                            errorMessage={errors.nameOfHost?.message}
                        />

                        <TextArea
                            label={t('Webinar.Schedule.Label.Description')}
                            {...register('description')}
                            textAreaClassNames="field-sizing-content"
                            errorMessage={errors.description?.message}
                        />

                        <Input
                            label={t('Webinar.Schedule.Label.ClientReference')}
                            {...register('clientReference')}
                            errorMessage={errors.clientReference?.message}
                        />

                        <div className="flex flex-wrap justify-between gap-4 mt-6 md:mt-10">
                            <Button
                                data-testid="webinar-save-button"
                                type="submit"
                                variant="primary"
                                isLoading={isPending}
                                disabled={isPending}
                                classNames={`${isPending && 'btn-disabled'}`}
                                onClick={storeWebinar}
                            >
                                {t('Generic.Button.SaveChanges')}
                            </Button>
                        </div>
                    </form>
                </WebinarCard>
            </div>
        </ContainerDefault>
    );
};
